import { useMemo } from 'react';
// routes
import { paths } from 'src/routes/paths';
// locales
import { useLocales } from 'src/locales';
// components
import SvgColor from 'src/components/svg-color';
import { PERMISSIONS } from 'src/utils/constant';

// ----------------------------------------------------------------------

const icon = (name: string) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
);

const ICONS = {
  trang_chu: icon('trang_chu'),
  bao_cao: icon('bao_cao'),
  lop_hoc: icon('lop_hoc'),
  an_pham: icon('an_pham'),
  ban_doc: icon('ban_doc'),
  muon_tra: icon('muon_tra'),
  kho_ke: icon('kho_ke'),
  nhap_xuat: icon('nhap_xuat'),
  chat_ai: icon('chat_ai'),
  chuyen_doi: icon('chuyen_doi'),
  cai_dat: icon('cai_dat'),
  quyen_han: icon('quyen_han'),
  nguoi_dung: icon('nguoi_dung'),
  muc_phi_phat: icon('muc_phi_phat'),
  yeu_cau: icon('yeu_cau'),
  mat_sach: icon('mat_sach'),
  phi_mo_the: icon('phi-mo-the'),
  dk_mo_the: icon('dk_mo_the'),
  truong_hoc: icon('truong-hoc'),
  dk_muon_truoc: icon('dk_muon_truoc'),
};

// ----------------------------------------------------------------------

export function useNavData() {
  const { t } = useLocales();

  const data = useMemo(
    () => [
      // Trang chủ

      // ----------------------------------------------------------------------
      {
        subheader: t('Trang chủ'),
        forReader: false,
        items: [
          {
            title: t('Trang chủ'),
            path: paths.home.trang_chu,
            icon: ICONS.trang_chu,
          },
          {
            title: t('Báo cáo'),
            path: paths.bao_cao.root,
            icon: ICONS.bao_cao,
            children: [
              {
                title: t('Báo cáo mượn trả'),
                path: paths.bao_cao.muon_tra,
              },
              {
                title: t('Thống kê bạn đọc'),
                path: paths.bao_cao.ban_doc,
              },
              {
                title: t('Thống kê ấn phẩm'),
                path: paths.bao_cao.an_pham,
              },
            ],
          },
        ],
      },
      // Dành cho bạn đọc
      {
        subheader: t('Chung'),
        forReader: true,
        items: [
          {
            title: t('Tra cứu ấn phẩm'),
            path: paths.danh_cho_ban_doc.tra_cuu,
            icon: ICONS.an_pham,
          },
          {
            title: t('Diễn đàn'),
            path: paths.danh_cho_ban_doc.dien_dan,
            icon: ICONS.dk_mo_the,
          },
        ],
      },
      // {
      //   subheader: t('Ấn phẩm đóng góp'),
      //   forReader: true,
      //   items: [
      //     {
      //       title: t('Danh sách'),
      //       path: paths.danh_cho_ban_doc.dong_gop_an_pham.danh_sach,
      //       icon: ICONS.mat_sach,
      //     },
      //     {
      //       title: t('Tạo đóng góp'),
      //       path: paths.danh_cho_ban_doc.dong_gop_an_pham.tao,
      //       icon: ICONS.phi_mo_the,
      //     },
      //   ],
      // },
      // Danh mục
      {
        subheader: t('Danh mục'),
        childrenRules: [
          PERMISSIONS.CLASS_VIEW,
          PERMISSIONS.BOOK_VIEW,
          PERMISSIONS.PUBLISHER_VIEW,
          PERMISSIONS.CATEGORY_VIEW,
          PERMISSIONS.FIELD_VIEW,
          PERMISSIONS.LANGUAGE_VIEW,
          PERMISSIONS.BOOK_STATUS_VIEW,
          PERMISSIONS.USER_VIEW,
          PERMISSIONS.LOAN_RECEIPT_VIEW,
          PERMISSIONS.PENALTY_TICKET_VIEW,
          PERMISSIONS.POSITION_VIEW,
          PERMISSIONS.FINE_POLICY_VIEW,
          PERMISSIONS.BOOK_REQUEST_VIEW,
          PERMISSIONS.BOOK_LOST_VIEW,
          PERMISSIONS.READER_GROUP_VIEW,
          PERMISSIONS.FORM_REGISTER_VIEW,
        ],
        items: [
          {
            title: t('Lớp học'),
            path: paths.lop_hoc.root,
            icon: ICONS.lop_hoc,
            rule: PERMISSIONS.CLASS_VIEW,
            children: [
              {
                title: t('Danh sách lớp học'),
                path: paths.lop_hoc.danh_sach,
              },
              {
                title: t('Niên khoá lớp học'),
                path: paths.lop_hoc.nien_khoa,
              },
            ],
          },
          {
            title: t('Ấn phẩm'),
            path: paths.an_pham.root,
            icon: ICONS.an_pham,
            rule: [
              PERMISSIONS.BOOK_VIEW,
              PERMISSIONS.PUBLISHER_VIEW,
              PERMISSIONS.CATEGORY_VIEW,
              PERMISSIONS.FIELD_VIEW,
              PERMISSIONS.LANGUAGE_VIEW,
              PERMISSIONS.BOOK_STATUS_VIEW,
            ],
            children: [
              {
                title: t('Danh sách ấn phẩm'),
                path: paths.an_pham.danh_sach,
                rule: PERMISSIONS.BOOK_VIEW,
              },
              {
                title: t('Nhà xuất bản ấn phẩm'),
                path: paths.an_pham.nha_xuat_ban,
                rule: PERMISSIONS.PUBLISHER_VIEW,
              },
              {
                title: t('Danh mục ấn phẩm'),
                path: paths.an_pham.danh_muc,
                rule: PERMISSIONS.CATEGORY_VIEW,
              },
              {
                title: t('Lĩnh vực ấn phẩm'),
                path: paths.an_pham.linh_vuc,
                rule: PERMISSIONS.FIELD_VIEW,
              },
              {
                title: t('Ngôn ngữ ấn phẩm'),
                path: paths.an_pham.ngon_ngu,
                rule: PERMISSIONS.LANGUAGE_VIEW,
              },
              {
                title: t('Tình trạng ấn phẩm'),
                path: paths.an_pham.tinh_trang,
                rule: PERMISSIONS.BOOK_STATUS_VIEW,
              },
            ],
          },
          {
            title: t('Bạn đọc'),
            path: paths.ban_doc.root,
            icon: ICONS.ban_doc,
            rule: [PERMISSIONS.USER_VIEW, PERMISSIONS.READER_GROUP_VIEW],
            children: [
              {
                title: t('Danh sách bạn đọc'),
                path: paths.ban_doc.danh_sach,
                rule: PERMISSIONS.USER_VIEW,
              },
              {
                title: t('Nhóm bạn đọc'),
                rule: PERMISSIONS.READER_GROUP_VIEW,

                path: paths.ban_doc.nhom,
              },
            ],
          },
          {
            title: t('Mượn trả'),
            path: paths.muon_tra.root,
            icon: ICONS.muon_tra,
            rule: [PERMISSIONS.LOAN_RECEIPT_VIEW, PERMISSIONS.PENALTY_TICKET_VIEW],
            children: [
              {
                title: t('Danh sách mượn trả'),
                path: paths.muon_tra.danh_sach,
                rule: PERMISSIONS.LOAN_RECEIPT_VIEW,
              },
              {
                title: t('Danh sách mượn quá hạn'),
                path: paths.muon_tra.qua_han,
                rule: PERMISSIONS.LOAN_RECEIPT_VIEW,
              },
              {
                title: t('Danh sách phiếu phạt'),
                path: paths.muon_tra.phieu_phat,
                rule: PERMISSIONS.PENALTY_TICKET_VIEW,
              },
            ],
          },
          {
            title: t('Kho kệ'),
            path: paths.kho_ke.root,
            icon: ICONS.kho_ke,

            rule: PERMISSIONS.POSITION_VIEW,
            children: [
              {
                title: t('Danh sách kệ hàng'),
                path: paths.kho_ke.ke_hang,
              },
            ],
          },
          {
            title: t('Chính sách phạt'),
            path: paths.phi_phat.root,
            icon: ICONS.muc_phi_phat,
            rule: PERMISSIONS.FINE_POLICY_VIEW,
            children: [
              {
                title: t('Danh sách chính sách phạt'),
                path: paths.phi_phat.danh_sach,
              },
              {
                title: t('Danh sách chi tiết phạt'),
                path: paths.phi_phat.chi_tiet_phat,
              },
            ],
          },
          {
            title: t('Đk mượn trước'),
            path: paths.dk_muon_truoc.danh_sach,
            icon: ICONS.dk_muon_truoc,
            rule: PERMISSIONS.LOAN_RECEIPT_VIEW,
          },
          {
            title: t('Yêu cầu'),
            path: paths.yeu_cau.danh_sach,
            icon: ICONS.yeu_cau,
            rule: PERMISSIONS.BOOK_REQUEST_VIEW,
          },
          {
            title: t('Mất ấn phẩm'),
            path: paths.mat_sach.danh_sach,
            icon: ICONS.mat_sach,
            rule: PERMISSIONS.BOOK_LOST_VIEW,
          },
          {
            title: t('Phí mở thẻ'),
            path: paths.phi_mo_the.danh_sach,
            icon: ICONS.phi_mo_the,
          },
          {
            title: t('ĐK mở thẻ'),
            path: paths.dang_ky_mo_the.danh_sach,
            icon: ICONS.dk_mo_the,
            rule: PERMISSIONS.FORM_REGISTER_VIEW,
          },
        ],
      },

      // Hệ thống
      {
        subheader: t('Hệ thống'),
        childrenRules: [
          PERMISSIONS.ACCOUNT_VIEW,
          PERMISSIONS.SETTING_VIEW,
          PERMISSIONS.SCHOOL_UPDATE,
        ],
        items: [
          {
            title: t('Quyền hạn'),
            path: paths.quyen_han.root,
            icon: ICONS.quyen_han,
            rule: PERMISSIONS.ACCOUNT_VIEW,
            children: [
              {
                title: t('Danh sách chức năng'),
                path: paths.quyen_han.danh_sach,
              },
              {
                title: t('Danh sách các quyền'),
                path: paths.quyen_han.danh_sach_cac_quyen,
              },
            ],
          },

          {
            title: t('Người dùng'),
            path: paths.nguoi_dung.root,
            icon: ICONS.nguoi_dung,
            rule: PERMISSIONS.ACCOUNT_VIEW,
            children: [
              {
                title: t('Danh sách người dùng'),
                path: paths.nguoi_dung.danh_sach,
              },
              {
                title: t('Danh sách tài khoản'),
                path: paths.nguoi_dung.danh_sach_tai_khoan,
              },
            ],
          },
          {
            title: t('Trường học'),
            path: paths.truong_hoc.root,
            icon: ICONS.truong_hoc,
            rule: PERMISSIONS.SCHOOL_UPDATE,
          },
          {
            title: t('Cài đặt'),
            path: paths.cai_dat.root,
            icon: ICONS.cai_dat,
            rule: PERMISSIONS.SETTING_UPDATE,
          },
        ],
      },
    ],
    [t]
  );

  return data;
}
